import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { heroData } from '../data/mock';
import { ArrowRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const HeroSection = () => {
  const sectionRef = useRef(null);
  const headlineRef = useRef(null);
  const accentRef = useRef(null);
  const subheadRef = useRef(null);
  const ctaRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start']
  });

  // Disable parallax on mobile for better performance
  const y = useTransform(scrollYProgress, [0, 1], [0, isMobile ? 0 : 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, isMobile ? 1 : 0]);

  useEffect(() => {
    // Skip GSAP animations on mobile for better performance
    if (isMobile) {
      // Just show elements immediately on mobile
      if (headlineRef.current) headlineRef.current.style.opacity = '1';
      if (accentRef.current) accentRef.current.style.opacity = '1';
      if (subheadRef.current) subheadRef.current.style.opacity = '1';
      if (ctaRef.current) {
        Array.from(ctaRef.current.children).forEach(child => {
          child.style.opacity = '1';
        });
      }
      return;
    }

    const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

    // Headline animation - reduced duration for better performance
    tl.fromTo(
      headlineRef.current,
      { y: 60, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, delay: 0.2 }
    )
    .fromTo(
      accentRef.current,
      { y: 60, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8 },
      '-=0.5'
    )
    .fromTo(
      subheadRef.current,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6 },
      '-=0.4'
    )
    .fromTo(
      ctaRef.current.children,
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, stagger: 0.1 },
      '-=0.3'
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isMobile]);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex flex-col justify-center items-center pt-28 md:pt-36 lg:pt-40"
    >
      {/* Grid Pattern Background */}
      <div className="grid-pattern" />

      {/* Gradient Orbs */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#D3FF62]/20 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#004534]/30 rounded-full blur-[128px] pointer-events-none" />

      <motion.div
        style={{ y, opacity }}
        className="w-full relative z-10 flex flex-col items-center justify-center -mt-[60px] md:-mt-[100px] px-8 md:px-16 lg:px-20"
      >
        {/* Main Content - Centered in viewport */}
        <div className="w-full text-center">
          {/* Headline - More Dynamic */}
          <div className="overflow-hidden mb-2">
            <h1
              ref={headlineRef}
              className="display-hero text-white leading-none tracking-[-0.05em]"
            >
              THE FUTURE
            </h1>
          </div>
          
          <div className="overflow-hidden mb-2">
            <h1
              ref={accentRef}
              className="display-hero leading-none tracking-[-0.05em]"
              style={{ 
                WebkitTextStroke: '2px #D3FF62',
                WebkitTextFillColor: 'transparent'
              }}
            >
              OF SPORTS
            </h1>
          </div>

          <div className="overflow-hidden mb-10">
            <motion.h1
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="display-hero gradient-text leading-none tracking-[-0.05em]"
            >
              TECHNOLOGY
            </motion.h1>
          </div>

          {/* Subheadline */}
          <p
            ref={subheadRef}
            className="body-large text-white/70 max-w-2xl mx-auto mb-12"
          >
            {heroData.subheadline}
          </p>

          {/* CTAs */}
          <div ref={ctaRef} className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a href="#ventures" className="btn-primary magnetic-btn text-sm sm:text-base">
              {heroData.ctaText}
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </motion.div>

      {/* Scroll Indicator - Positioned higher */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 md:bottom-16 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-white/50 text-xs uppercase tracking-widest hidden sm:block">Scroll</span>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          className="w-5 h-8 sm:w-6 sm:h-10 rounded-full border-2 border-white/30 flex justify-center pt-1.5 sm:pt-2"
        >
          <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-[#D3FF62]" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
